<template>
    <div>
        <b-modal id="modalAddJob" size="lg" hide-footer hide-header centered no-close-on-backdrop
            no-enforce-focus lazy content-class="custom-modal-content" modal-class="custom-modal-wrapper"
            @shown="onModalShown">
            <div class="modal-header !border-gray-100">Manage Job</div>
            <div class="manage-job">
                <div class="mb-3">
                    <div class="mb-1 title text-gray-600">Job Title</div>
                    <input type="text" class="form-control title-input border-gray-300" placeholder="Title"
                        v-model="formData.name" />
                </div>
                <div class="mb-3">
                    <div class="mb-1 description text-gray-600">Description</div>
                    <redactor 
                        ref="descriptionRedactor"
                        :placeholder="'Add a description...'"
                        :comment-api-url="commentFileApi"
                        :cur-users-taggable="curUsersTaggable"
                        :show-preview="false"
                        :show-source="true"
                        :use-payload-auth="true"
                        @input="updateDescriptionText"
                        class="description-input" />
                </div>
                <div class="d-flex align-items-center flex-wrap gap-2 toolbar-list">
                    <div class="dropdown dropdown-wrapper-js" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-title="Status">
                        <button
                            class="btn btn-light fs-12 px-2 py-1 fw-normal border-gray-100 d-flex align-items-center gap-1 shadow-none lh-1 dropdown-toggle-js rounded"
                            @click.stop="toggleOrCloseDropdown('status')">
                            <img :src="getJobStatusIconById(selectedStatus)" class="flex-shrink-0" width="14" alt="">
                            {{ getJobStatusLabelForId(selectedStatus) }}
                        </button>
                        <div class="dropdown-menu fh rounded-2 mt-1 py-0 border-gray-100 shadow-sm overflow-hidden dropdown-js"
                            :class="{ 'show': statusOptions }">
                            <ul class="list-unstyled mb-0 fs-14 list-group list-group-flush w-224">
                                <li class="list-group-item p-0 bg-white" v-for="status in jobStatuses" :key="status.id">
                                    <span class="dropdown-item text-gray-700 d-flex align-items-center gap-12 py-2 px-3"
                                        @click.prevent="selectStatus(status.id)">
                                        <img :src="getCategoryIcon(status.statusCategoryID)" width="20"
                                            class="flex-shrink-0 d-block" alt="status-icon">
                                        {{ status.name }}
                                    </span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div class="dropdown dropdown-wrapper-js" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-title="Priority">
                        <button
                            class="btn btn-light fs-12 px-1 py-1 fw-normal border-gray-100 d-flex align-items-center gap-1 shadow-none dropdown-toggle-js rounded"
                            @click.stop="toggleOrCloseDropdown('priority')">
                            <img :src="JOB_PRIORITY_META[selectedPriority] ? JOB_PRIORITY_META[selectedPriority].icon : '/img/block.svg'"
                                class="flex-shrink-0" width="14" alt="">
                        </button>
                        <div class="dropdown-menu rounded-3 mt-1 py-0 border-gray-100 shadow-sm overflow-hidden dropdown-js"
                            :class="{ 'show': priorityOptions }">
                            <ul class="list-unstyled mb-0 fs-14 list-group list-group-flush w-224">
                                <li class="list-group-item p-0 bg-white rounded-0"
                                    v-for="(meta, priority) in JOB_PRIORITY_META" :key="priority">
                                    <span class="dropdown-item text-gray-700 d-flex align-items-center gap-12 py-2 px-3"
                                        @click.prevent="selectPriority(Number(priority))">
                                        <img :src="meta.icon" width="20" class="flex-shrink-0 d-block rounded-0" alt="priority-icon">
                                        {{ meta.label }}
                                    </span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <!-- Start Date -->
                    <DatePickerButton ref="startDatePicker" :value="getFormattedDate(formData.startDate)" :is-open="startDatePickerOpen"
                        icon="/img/calendar-time.svg" tooltip="Start Date" ref-name="startDatePicker"
                        :date-type=JOB_DATE_TYPE.START
                        :other-date-value="getFormattedDate(formData.endDate)"
                        :other-date-type=JOB_DATE_TYPE.END
                        @input="handleDateSelect(CALENDAR_TYPE.START_DATE, $event)" @open="handleDatePickerOpen(CALENDAR_TYPE.START_DATE)"
                        @close="handleDatePickerClose(CALENDAR_TYPE.START_DATE)" />

                    <!-- End Date -->
                    <DatePickerButton ref="endDatePicker" :value="getFormattedDate(formData.endDate)" :is-open="endDatePickerOpen"
                        icon="/img/calendar-check.svg" tooltip="End Date" ref-name="endDatePicker"
                        :date-type=JOB_DATE_TYPE.END
                        :other-date-value="getFormattedDate(formData.startDate)"
                        :other-date-type=JOB_DATE_TYPE.START
                        @input="handleDateSelect(CALENDAR_TYPE.END_DATE, $event)" @open="handleDatePickerOpen(CALENDAR_TYPE.END_DATE)"
                        @close="handleDatePickerClose(CALENDAR_TYPE.END_DATE)" />

                    <div class="dropdown dropdown-wrapper-js" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-title="Assignee">
                        <button
                            class="btn btn-light fs-12 px-1 py-1 fw-normal d-flex align-items-center gap-1 shadow-none dropdown-toggle-js rounded"
                            :class="selectedAssignees.length ? 'border-0 bg-transparent px-0 py-0' : 'border-gray-100'"
                            @click.stop="toggleOrCloseDropdown('assignee')">
                            <JobAssigneeAvatars :assignees="selectedAssignees" :users="accountUsers" />
                        </button>
                        <div class="dropdown-menu fh rounded-3 mt-1 py-0 border-gray-100 shadow-sm overflow-hidden dropdown-js"
                            :class="{ 'show': assigneeOptions }">
                            <ul class="list-unstyled mb-0 fs-14 list-group list-group-flush w-224">
                                <li v-for="user in accountUsers" :key="user.id"
                                    class="list-group-item p-0 bg-white d-flex rounded-0">
                                    <span
                                        class="dropdown-item text-gray-700 d-flex align-items-center gap-12 py-2 px-3 w-100"
                                        :class="{ 'fw-semibold text-gray-900': selectedAssignees.includes(user.id) }"
                                        @click.prevent.stop="toggleAssignee(user.id)">
                                        <img :src="user.profileImageUrl || '/img/avatar.svg'" width="20" height="20"
                                            class="rounded-circle flex-shrink-0" alt="assignee-icon">
                                        <span class="assignee-name text-truncate ml-0">{{ user.name }}</span>
                                        <img v-if="selectedAssignees.includes(user.id)" src="/img/check.svg" width="16"
                                            class="mx-0 d-block" alt="">
                                    </span>
                                </li>
                                <li v-if="accountUsers.length === 0" class="list-group-item p-0 bg-white">
                                    <span class="dropdown-item text-gray-700 d-flex align-items-center gap-12 py-2 px-3">No users</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div class="dropdown dropdown-wrapper-js" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-title="Tags">
                        <button
                            class="btn btn-light fs-12 px-1 py-1 fw-normal border-gray-100 d-flex align-items-center gap-1 shadow-none dropdown-toggle-js rounded"
                            @click.stop="toggleOrCloseDropdown('tag')">
                            <img src="/img/tags.svg" class="flex-shrink-0" width="14" alt="">
                            <p v-if="getSelectedTagDisplayText()" class="mt-0 mb-0 fs-12">{{ getSelectedTagDisplayText() }}</p>
                        </button>
                        <div class="dropdown-menu rounded-3 mt-1 py-0 border-gray-100 shadow-sm overflow-hidden dropdown-js"
                            :class="{ 'show': tagOptions }" style="min-width: 280px;" @click.stop>
                            <div class="p-2 border-bottom border-gray-100 d-flex gap-2 align-items-center rounded-0">
                                <div class="position-relative flex-fill">
                                    <img src="/img/search.svg"
                                        class="search position-absolute start-0 top-50 translate-middle-y ml-2"
                                        width="20" alt="">
                                    <input type="text" class="form-control tag-search flex-fill border-gray-300 fs-14 fw-normal"
                                        placeholder="Search" :value="tagSearchQuery" @input="handleTagSearch"
                                        @keydown="handleTagKeydown">
                                </div>
                                <button v-b-modal.ManageTags
                                    class="add-btn btn border border-gray-200 p-0 rounded-circle box-32 d-flex align-items-center justify-content-center border-dashed">
                                    <img src="/img/plus-light.svg" class="d-block" width="16" alt="">
                                </button>
                            </div>
                            <div class="tags-scrollable-container" style="max-height: 200px; overflow-y: auto;">
                                <ul class="list-unstyled mb-0 fs-14 list-group list-group-flush w-280">
                                    <li v-for="tag in getFilteredTagsForJob()" :key="tag.id"
                                        class="list-group-item p-0 bg-white d-flex rounded-0">
                                        <span
                                            class="dropdown-item text-gray-700 d-flex align-items-center gap-12 py-2 px-3"
                                            @click.prevent.stop="toggleTag(tag)"
                                            :class="{ 'fw-semibold text-gray-900': isJobTagSelected(tag) }">
                                            <div class="dot"></div>
                                            {{ tag.name }}
                                            <img v-if="isJobTagSelected(tag)" src="/img/check.svg" width="16"
                                                class="ml-auto mx-3 d-block" alt="tags-check-icon">
                                        </span>
                                    </li>
                                    <li v-if="getFilteredTagsForJob().length === 0"
                                        class="list-group-item p-0 bg-white">
                                        <span
                                            class="dropdown-item text-gray-700 d-flex align-items-center gap-12 py-2 px-3">
                                            <div class="dot"></div>
                                            {{ tagSearchQuery ? 'No tags found' : 'No tags available' }}
                                        </span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="mt-4">
                    <JobFiles ref="jobFiles" :job-id="editJobId" :preset-files="presetFiles" />
                </div>

                <div class="mt-4">
                    <AssociatedContacts
                        :contacts="selectedContacts"
                        @update:contacts="selectedContacts = $event" />
                </div>
            </div>
            <div class="footer d-flex justify-content-end">
                <button class="cancel-btn text-ashore-black mr-3" @click="closeModal">Cancel</button>
                <button class="submit-btn bg-success-500" @click="submitJob">Submit</button>
            </div>
        </b-modal>

    </div>
</template>

<script>
import DatePickerButton from "../../components/job-item/datePickerButton.vue";
import JobAssigneeAvatars from "../job-item/job-assignee-avatars.vue";
import JobFiles from "../job-item/job-files.vue";
import AssociatedContacts from "../job-item/associated-contacts.vue";
import redactor from "../form/redactor.vue";

import { MODAL_NAMES, JOB_STATUS_META, JOB_PRIORITY_META, JOB_STATUS, VARIANT, TYPES_ENUM, CALENDAR_TYPE, JOB_DATE_TYPE, MIN_DATE } from "../../utils/constants";
import { handleDropdownPosition, formatTagDisplayText, createTaggableUsers, createTagOnEnter, addTargetBlankToLinks, getJobAssigneeIds } from "../../utils/helpers";

const { DANGER } = VARIANT;
const { STRING, OBJECT, NUMBER } = TYPES_ENUM;

export default {
    name: "modalAddJob",
    components: {
        DatePickerButton,
        JobAssigneeAvatars,
        JobFiles,
        AssociatedContacts,
        redactor
    },
    props: {
        defaultJobStatus: {
            type: Number,
        },
        accountUsersProp: {
            type: Array,
            default: () => []
        },
        jobTagsProp: {
            type: Array,
            default: () => []
        },
        jobStatusesProp: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            MODAL_NAMES,
            JOB_STATUS_META,
            JOB_PRIORITY_META,
            CALENDAR_TYPE,
            JOB_DATE_TYPE,
            accountUsers: [],
            statusOptions: false,
            priorityOptions: false,
            assigneeOptions: false,
            tagOptions: false,
            formData: {
                name: '',
                description: '',
                status: (this.defaultJobStatus != null ? this.defaultJobStatus : JOB_STATUS.NOT_STARTED),
                priority: 0,
                startDate: null,
                endDate: null,
                assignees: [],
                tags: []
            },
            selectedStatus: (this.defaultJobStatus != null ? this.defaultJobStatus : JOB_STATUS.NOT_STARTED),
            selectedPriority: 0,
            selectedAssignees: [],
            selectedTags: [],
            selectedTagIds: [],
            jobStatuses: [],
            jobTags: [],
            tagSearchQuery: '',
            startDatePickerOpen: false,
            endDatePickerOpen: false,
            originalJob: null,
            editJobId: null,
            presetFiles: [],
            selectedContacts: [],
            originalContactIds: []
        };
    },
    computed: {
        isEditMode() {
            return Boolean(this.editJobId);
        },
        curUsersTaggable: function () {
            return createTaggableUsers(this.$A.Auth, this.accountUsers);
        },
        commentFileApi: function () {
            if (this.$A.Auth && this.isEditMode && this.editJobId) {
                const authToken = this.$A.Auth.apiToken.token;
                let apiUrl= `/jobs/${this.editJobId}/files?access_token=${authToken}`;
                return apiUrl;
            }
            return "";
        },
    },
    async mounted() {
        // Initialize from props when available, otherwise fetch lazily on show
        this.accountUsers = Array.isArray(this.accountUsersProp) && this.accountUsersProp.length ? this.accountUsersProp : [];
        this.jobTags = Array.isArray(this.jobTagsProp) && this.jobTagsProp.length ? this.jobTagsProp : [];
        this.jobStatuses = Array.isArray(this.jobStatusesProp) && this.jobStatusesProp.length ? this.jobStatusesProp : [];
        const initialStatus = (this.defaultJobStatus != null
            ? this.defaultJobStatus
            : (this.jobStatuses.find(s => s.statusCategoryID === JOB_STATUS.TODO)?.id || null));
        if (initialStatus != null) this.selectStatus(initialStatus);
        window.addEventListener('resize', handleDropdownPosition);
        document.addEventListener('click', this.handleOutsideClick);
        this.$nextTick(() => {
            handleDropdownPosition();
        });
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleOutsideClick);
        window.removeEventListener('resize', handleDropdownPosition);
    },
    watch: {
        accountUsersProp(newVal) {
            if (Array.isArray(newVal) && newVal.length) this.accountUsers = newVal;
        },
        jobTagsProp(newVal) {
            if (Array.isArray(newVal)) this.jobTags = newVal;
        },
        jobStatusesProp(newVal) {
            if (Array.isArray(newVal) && newVal.length) this.jobStatuses = newVal;
        },
        defaultJobStatus(newVal) {
            const statusToSet = (newVal != null ? newVal : (this.jobStatuses.find(s => s.statusCategoryID === JOB_STATUS.TODO)?.id || null));
            if (statusToSet != null) this.selectStatus(statusToSet);
        }
    },
    methods: {
        createTaggableUsers,
        handleDropdownPosition,
        closeAllDatePickers() {
            this.startDatePickerOpen = false;
            this.endDatePickerOpen = false;
        },
        handleOutsideClick(event) {
            if (event.target.closest('.mx-calendar-header') || event.target.closest('.mx-calendar-panel-year') || event.target.closest('.mx-calendar-panel-month')) {
                return;
            }
            this.closeAllDropdowns();
            this.closeAllDatePickers();
        },
        handleDatePickerOpen(type) {
            this.closeAllDatePickers();
            this.closeAllDropdowns();
            if (type === CALENDAR_TYPE.START_DATE) {
                this.startDatePickerOpen = true;
            } else if (type === CALENDAR_TYPE.END_DATE) {
                this.endDatePickerOpen = true;
            }
        },
        handleDatePickerClose(type) {
            if (type === CALENDAR_TYPE.START_DATE) {
                this.startDatePickerOpen = false;
            } else if (type === CALENDAR_TYPE.END_DATE) {
                this.endDatePickerOpen = false;
            }
        },
        closeModal() {
            this.$bvModal.hide(MODAL_NAMES.ADD_JOB_MODAL);
            this.closeAllDatePickers();
            this.closeAllDropdowns();
            this.resetForm();
        },
        openWithJob(job, options = {}) {
            if (!job) return;

            // presetFiles: files to show in the Files section before the job exists
            // (e.g., files from an Artwork Intake submission being converted to a job).
            this.presetFiles = Array.isArray(options.presetFiles) ? options.presetFiles : [];

            // Store original job data for tag comparison
            this.originalJob = { ...job };
            
            // Prefill form with existing job data
            this.editJobId = job.id;
            const tags = this.extractTagIds(job);

            const startDate = (job.startDate && job.startDate !== MIN_DATE) ? job.startDate : null;
            const endDate = (job.endDate && job.endDate !== MIN_DATE) ? job.endDate : null;

            this.formData = {
                name: job.name || '',
                description: job.description || '',
                status: job.status || null,
                priority: typeof job.priority === NUMBER ? job.priority : 0,
                startDate: startDate,
                endDate: endDate,
                assignees: [...getJobAssigneeIds(job)],
                tags: tags
            };
            this.selectedStatus = this.formData.status;
            this.selectedPriority = this.formData.priority;
            this.selectedAssignees = this.formData.assignees;
            this.selectedTagIds = [...tags];
            this.selectedTags = this.jobTags.filter(tag => tags.includes(tag.id));
            
            // Set the initial text in the redactor
            this.$nextTick(() => {
                const redactor = this.$refs.descriptionRedactor;
                if (redactor && redactor.setTextBody) {
                    redactor.setTextBody(this.formData.description);
                }
            });
            
            this.$bvModal.show(MODAL_NAMES.ADD_JOB_MODAL);

            // Hydrate contacts for edit mode (caller-supplied takes precedence, otherwise fetch).
            this.selectedContacts = Array.isArray(job.contacts) ? [...job.contacts] : [];
            this.originalContactIds = this.selectedContacts.map((c) => c.id);
            if (!Array.isArray(job.contacts)) {
                this.$A.JobService.getJobById(job.id).then((res) => {
                    if (this.editJobId !== job.id) return; // modal was reset/reopened
                    const list = Array.isArray(res?.contacts) ? res.contacts : [];
                    this.selectedContacts = list;
                    this.originalContactIds = list.map((c) => c.id);
                }).catch((e) => {
                    console.error("Failed to load contacts for edit:", e);
                });
            }
        },
        extractTagIds(job) {
            const candidate = job?.tags || job?.jobTags || job?.tagIds || job?.taggedItems || job?.tagLinks || [];
            const idByName = {};
            (this.jobTags || []).forEach(t => { idByName[t.name] = t.id; });
            if (!Array.isArray(candidate) || candidate.length === 0) return [];
            const first = candidate[0];
            if (typeof first === NUMBER) return candidate;
            if (typeof first === STRING) return candidate.map(n => idByName[n]).filter(Boolean);
            if (first && typeof first === OBJECT) {
                if ('tagId' in first) return candidate.map(l => l.tagId).filter(Boolean);
                if ('id' in first) return candidate.map(t => t.id).filter(Boolean);
            }
            return [];
        },
        enableBodyScroll() {
            document.body.style.overflow = "auto";
        },
        async loadJobTags() {
            try {
                if (this.jobTags && this.jobTags.length) return; // already provided
                this.jobTags = await this.$A.JobTagService.GetAll();
            } catch (e) {
                this.jobTags = [];
                console.error('Failed to load job tags in modal:', e);
            }
        },
        closeAllDropdowns() {
            this.statusOptions = false;
            this.priorityOptions = false;
            this.assigneeOptions = false;
            this.tagOptions = false;
            this.closeAllDatePickers();
        },
        closeDropdown(type) {
            this[`${type}Options`] = false;
        },
        toggleOrCloseDropdown(type) {
            const key = `${type}Options`;
            // If open, close it
            if (this[key]) {
                this[key] = false;
            } else {
                this.closeAllDropdowns(); // Close others
                this[key] = true;
            }
        },
        selectStatus(status) {
            const statusId = typeof status === OBJECT ? status.id : status;
            this.selectedStatus = statusId;
            this.formData.status = statusId;
            this.statusOptions = false;
        },
        selectPriority(priority) {
            this.selectedPriority = priority;
            this.formData.priority = priority;
            this.priorityOptions = false;
        },
        toggleAssignee(userId) {
            this.selectedAssignees = this.selectedAssignees.includes(userId)
                ? this.selectedAssignees.filter(id => id !== userId)
                : [...this.selectedAssignees, userId];
            this.formData.assignees = this.selectedAssignees;
        },
        removeTag(tag) {
            this.selectedTags = this.selectedTags.filter(t => t.id !== tag.id);
            this.selectedTagIds = this.selectedTagIds.filter(id => id !== tag.id);
            this.formData.tags = [...this.selectedTagIds];
        },
        resetForm() {
            this.formData = {
                name: '',
                description: '',
                status: (this.defaultJobStatus != null ? this.defaultJobStatus : JOB_STATUS.NOT_STARTED),
                priority: 0,
                startDate: null,
                endDate: null,
                assignees: [],
                tags: []
            };
            this.selectedStatus = (this.defaultJobStatus != null ? this.defaultJobStatus : JOB_STATUS.NOT_STARTED);
            this.selectedPriority = 0;
            this.selectedAssignees = [];
            this.selectedTags = [];
            this.selectedTagIds = [];
            this.tagSearchQuery = '';
            this.editJobId = null;
            this.originalJob = null;
            this.presetFiles = [];
            this.selectedContacts = [];
            this.originalContactIds = [];
            
            // Clear the redactor content
            this.$nextTick(() => {
                const redactor = this.$refs.descriptionRedactor;
                if (redactor && redactor.setTextBody) {
                    redactor.setTextBody('');
                }
            });
        },
        // Date helpers
        getFormattedDate(dateValue) {
            const displayValue = (dateValue && dateValue !== MIN_DATE) ? dateValue : null;
            return displayValue;
        },
        handleDateSelect(type, value) {
            this.formData[type] = value;
        },
        onModalShow() {
            const statusToSet = (this.defaultJobStatus != null ? this.defaultJobStatus : JOB_STATUS.NOT_STARTED);
            this.selectStatus(statusToSet);
        },
        onModalShown() {
            this.enableBodyScroll();
            // Lazy fetch only when props weren't provided
            if (!this.jobStatuses || this.jobStatuses.length === 0) {
                this.$A.JobStatusService.getAllJobStatuses().then(s => { this.jobStatuses = s || []; }).catch(() => { this.jobStatuses = []; });
            }
            if (!this.accountUsers || this.accountUsers.length === 0) {
                this.$A.AccountService.All().then(u => { this.accountUsers = u || []; }).catch(() => { this.accountUsers = []; });
            }
            this.loadJobTags();
            this.$nextTick(() => {
                handleDropdownPosition();
            });
        },
        async submitJob() {
            try {
                // Validate required fields
                if (!this.formData.name.trim()) {
                    this.$toast('Job title is required', {
                        title: 'Validation Error',
                        variant: DANGER,
                    });
                    return;
                }

                if (this.isEditMode) {
                    // For editing, use the new tag mapping structure
                    const originalTagIds = this.extractTagIds(this.originalJob || {});
                    const currentTagIds = this.selectedTagIds || [];

                    // Calculate tags to add (update) and remove (delete)
                    const tagsToAdd = currentTagIds.filter(id => !originalTagIds.includes(id));
                    const tagsToRemove = originalTagIds.filter(id => !currentTagIds.includes(id));

                    const currentContactIds = this.selectedContacts.map((c) => c.id);
                    const contactsToAdd = currentContactIds.filter((id) => !this.originalContactIds.includes(id));
                    const contactsToRemove = this.originalContactIds.filter((id) => !currentContactIds.includes(id));

                    const processedDescription = addTargetBlankToLinks(this.$refs.descriptionRedactor?.getTextBody?.() || this.formData.description);

                    const jobData = {
                        name: this.formData.name,
                        description: processedDescription,
                        assignees: this.formData.assignees,
                        status: this.formData.status,
                        priority: this.formData.priority,
                        startDate: this.formData.startDate,
                        endDate: this.formData.endDate,
                        tags: {
                            update: tagsToAdd,
                            delete: tagsToRemove
                        },
                        _contactsToAdd: contactsToAdd,
                        _contactsToRemove: contactsToRemove,
                    };

                    // Emit event to parent for update
                    this.$emit('job-edited', { jobId: this.editJobId, jobData });
                } else {
                    const processedDescription = addTargetBlankToLinks(this.$refs.descriptionRedactor?.getTextBody?.() || this.formData.description);
                    
                    const pendingFiles = this.$refs.jobFiles?.extractPending?.() || [];
                    const pendingContactIds = this.selectedContacts.map((c) => c.id);

                    const newJob = {
                        name: this.formData.name,
                        description: processedDescription,
                        assignees: this.formData.assignees,
                        status: this.formData.status,
                        priority: this.formData.priority,
                        startDate: this.formData.startDate,
                        endDate: this.formData.endDate,
                        tags: this.selectedTagIds && this.selectedTagIds.length ? this.selectedTagIds : [],
                        _pendingFiles: pendingFiles,
                        _pendingContactIds: pendingContactIds,
                    };

                    // Emit event to parent component to handle job creation
                    this.$emit('job-created', newJob);
                }

                this.closeModal();
            } catch (error) {
                console.error('Error creating job:', error);
                this.$toast('Failed to create job', {
                    title: 'Error',
                    variant: DANGER,
                });
            }
        },
        // Tag helpers (similar to JobColumn)
        handleTagSearch(e) {
            this.tagSearchQuery = (e && e.target ? e.target.value : e) || '';
        },
        handleTagKeydown(event) {
            createTagOnEnter(
                event, 
                this.tagSearchQuery, 
                this.jobTags, 
                this.$A.JobTagService,
                () => this.tagSearchQuery = '',
                (message) => this.$toast(message, { title: 'Error', variant: 'danger' })
            );
        },
        getFilteredTagsForJob() {
            const query = (this.tagSearchQuery || '').toLowerCase().trim();
            if (!query) return this.jobTags;
            return this.jobTags.filter(tag => (tag.name || '').toLowerCase().includes(query));
        },
        isJobTagSelected(tag) {
            return this.selectedTagIds.includes(tag.id);
        },
        toggleTag(tag) {
            if (this.isJobTagSelected(tag)) {
                this.selectedTagIds = this.selectedTagIds.filter(id => id !== tag.id);
                this.selectedTags = this.selectedTags.filter(t => t.id !== tag.id);
            } else {
                this.selectedTagIds = [...this.selectedTagIds, tag.id];
                this.selectedTags = [...this.selectedTags, tag];
            }
            this.formData.tags = [...this.selectedTagIds];
        },
        getSelectedTagNames() {
            return (this.selectedTags || []).map(t => t.name).filter(Boolean);
        },
        getCategoryIcon(categoryId) {
            const meta = JOB_STATUS_META[categoryId];
            return meta ? meta.icon : '/img/block.svg';
        },
        getJobStatusIconById(statusId) {
            const s = this.jobStatuses.find(x => x.id === statusId);
            return this.getCategoryIcon(s?.statusCategoryID);
        },
        getJobStatusLabelForId(statusId) {
            const s = this.jobStatuses.find(x => x.id === statusId);
            return s ? s.name : 'Unknown Status';
        },
        getSelectedTagDisplayText() {
            return formatTagDisplayText(this.getSelectedTagNames());
        },
        updateDescriptionText(htmlContent) {
            this.formData.description = htmlContent;
        }
    },
};
</script>

<style scoped>
.add-btn {
    border-width: 2px !important;
    border-radius: 16px !important;
}

::v-deep [data-command="loom.toggle"] {
  display: none !important;
}

::v-deep .rx-container {
    border-color: #B5B5B8 !important;
}

::v-deep .description-input .progress {
    display: none !important;
}

::v-deep .rx-toolbar-container.is-rx-toolbar {
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
    border-color: #B5B5B8 !important;
}

.tag-search {
    padding-left: 32px;
}

.assignee-name {
    width: 140px;
}

.w-224 {
    width: 224px !important;
}

.w-280 {
    width: 280px !important;
}

.tags-scrollable-container {
    max-height: 165px !important;
    overflow-y: auto;
    border-radius: 0px !important;
}

.search.top-50 {
    top: 50% !important;
}

.modal-open {
    overflow: hidden !important;
}

.modal-open .modal {
    overflow: hidden !important;
}

::v-deep .modal-body {
    padding: 0px !important;
}

::v-deep .custom-modal-wrapper {
    position: fixed !important;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    overflow: auto !important;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 !important;
}

::v-deep .custom-modal-content {
    height: auto;
    margin: auto;
    max-height: fit-content;
}

.modal-header {
    font-size: 18px;
    font-weight: 500;
    font-style: normal;
    color: #3A3A3A;
    line-height: 27px;
    padding: 16px;
}

.manage-job {
    padding: 24px 16px;
    border-bottom: 1px solid #EBEBEB;
    border-top: 1px solid #EBEBEB;
}

.title {
    font-family: 'Open Sans';
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
}

.title-input {
    padding: 9px 13px;
    border-radius: 6px;
    font-size: 16px;
    box-shadow: none;
}

.description-input {
    border-radius: 6px;
}

.footer {
    padding: 16px;
}

.cancel-btn {
    padding: 7px 11px;
    border-radius: 4px;
    border: none;
    background-color: #EEF2F5;
}

.submit-btn {
    padding: 7px 11px;
    border-radius: 4px;
    border: none;
    color: #FFFFFF;
}

.dropdown-menu.fh {
    max-height: 200px !important;
    overflow-y: auto !important;
}

.rounded-0 {
    border-radius: 0 !important;
}


@media (max-width: 768px) {
    .dropdown-menu {
        top: auto;
        bottom: 0;
        box-shadow: 0px 9px 28px 8px rgba(0, 0, 0, 0.05) !important;
        box-shadow: 0px 3px 6px -4px rgba(0, 0, 0, 0.12) !important;
        box-shadow: 0px 6px 16px 0px rgba(0, 0, 0, 0.08) !important;
    }
}
</style>
